import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronDown,
  Shield,
  X
} from 'lucide-react'

const STORAGE_KEY = 'adminSidebarCollapsedGroups'

function getInitialCollapsedGroups() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

function itemClassName(active) {
  return `flex min-h-9 min-w-0 items-center gap-2.5 border-l-2 px-3 py-2 text-sm font-medium ${
    active
      ? 'border-cyan-500 bg-gray-800 text-white hover:text-white dark:text-white dark:hover:text-white'
      : 'border-transparent text-gray-300 hover:border-gray-500 hover:bg-gray-800 hover:text-white dark:text-gray-300 dark:hover:text-white'
  }`
}

function ItemBadge({ label, tone = 'warning' }) {
  if (!label) return null
  const toneClasses = {
    critical:
      'border-red-700 bg-red-950 text-red-200',
    warning:
      'border-amber-700 bg-amber-950 text-amber-200',
    neutral:
      'border-gray-600 bg-gray-900 text-gray-300'
  }

  return (
    <span
      className={`flex-none border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
        toneClasses[tone] || toneClasses.warning
      }`}
    >
      {label}
    </span>
  )
}

export default function AdminSidebar({
  groups,
  mobileOpen,
  onClose,
  onNavigate
}) {
  const location = useLocation()
  const navigationRef = useRef(null)
  const [collapsedGroups, setCollapsedGroups] = useState(
    getInitialCollapsedGroups
  )

  const activeGroupId = useMemo(
    () =>
      groups.find(group =>
        group.items.some(
          item =>
            item.path === location.pathname ||
            location.pathname.startsWith(`${item.path}/`)
        )
      )?.id,
    [groups, location.pathname]
  )
  const exactMatchPaths = useMemo(() => {
    const paths = groups.flatMap(group =>
      group.items.map(item => item.path)
    )
    return new Set(
      paths.filter(path =>
        paths.some(
          candidate =>
            candidate !== path &&
            candidate.startsWith(`${path}/`)
        )
      )
    )
  }, [groups])

  useEffect(() => {
    if (!activeGroupId) return
    setCollapsedGroups(current =>
      current[activeGroupId]
        ? { ...current, [activeGroupId]: false }
        : current
    )
  }, [activeGroupId])

  useEffect(() => {
    const navigation = navigationRef.current
    const activeLink = navigation?.querySelector(
      'a[aria-current="page"]'
    )
    if (!navigation || !activeLink) return undefined

    const frame = window.requestAnimationFrame(() => {
      const navigationBox =
        navigation.getBoundingClientRect()
      const activeBox = activeLink.getBoundingClientRect()
      const edgePadding = 12

      if (activeBox.top < navigationBox.top) {
        navigation.scrollTop -=
          navigationBox.top - activeBox.top + edgePadding
      } else if (
        activeBox.bottom > navigationBox.bottom
      ) {
        navigation.scrollTop +=
          activeBox.bottom -
          navigationBox.bottom +
          edgePadding
      }
    })

    return () => window.cancelAnimationFrame(frame)
  }, [collapsedGroups, location.pathname])

  const toggleGroup = groupId => {
    setCollapsedGroups(current => {
      const next = {
        ...current,
        [groupId]: !current[groupId]
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // Presentation state can safely fall back to the in-memory value.
      }
      return next
    })
  }

  const handleNavigate = () => {
    onNavigate?.()
    onClose?.()
  }

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex h-screen w-72 flex-none transform flex-col border-r border-gray-800 bg-gray-950 text-white transition-transform duration-200 md:relative md:z-20 md:translate-x-0 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      aria-label="Platform Console navigation"
    >
      <div className="flex h-16 flex-none items-center justify-between border-b border-gray-800 px-4">
        <Link
          to="/admin"
          onClick={handleNavigate}
          className="flex min-w-0 items-center gap-3 text-white hover:text-white dark:text-white dark:hover:text-white"
        >
          <span className="flex h-9 w-9 flex-none items-center justify-center bg-cyan-500 text-gray-950">
            <Shield className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">
              x3algo
            </span>
            <span className="block truncate text-xs text-gray-400">
              Platform Console
            </span>
          </span>
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:bg-gray-800 hover:text-white md:hidden"
          aria-label="Close Platform Console navigation"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <nav
        ref={navigationRef}
        className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4"
        aria-label="Platform Console destinations"
      >
        {groups.map(group => {
          const collapsed = Boolean(collapsedGroups[group.id])
          return (
            <section key={group.id} className="mb-4">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="flex min-h-8 w-full items-center justify-between px-3 text-left text-[11px] font-semibold uppercase text-gray-500 hover:text-gray-300"
                aria-expanded={!collapsed}
                aria-controls={`admin-nav-${group.id}`}
              >
                <span>{group.label}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${
                    collapsed ? '-rotate-90' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
              {!collapsed && (
                <ul id={`admin-nav-${group.id}`} className="mt-1 space-y-0.5">
                  {group.items.map(item => {
                    const Icon = item.icon

                    return (
                      <li key={item.id}>
                        <NavLink
                          to={item.path}
                          end={exactMatchPaths.has(item.path)}
                          onClick={handleNavigate}
                          className={({ isActive }) =>
                            itemClassName(isActive)
                          }
                        >
                          <Icon
                            className="h-4 w-4 flex-none"
                            aria-hidden="true"
                          />
                          <span className="min-w-0 flex-1 truncate">
                            {item.label}
                          </span>
                          <ItemBadge
                            label={item.badge}
                            tone={item.badgeTone}
                          />
                        </NavLink>
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>
          )
        })}
      </nav>

      <div className="flex-none border-t border-gray-800 p-3">
        <Link
          to="/"
          onClick={handleNavigate}
          className="flex min-h-10 items-center gap-2.5 px-3 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white dark:text-gray-300 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Trading dashboard
        </Link>
      </div>
    </aside>
  )
}
